/**
 * Profile strength and the passport / ID upload → their locale keys, for the
 * web portal and the mobile app alike.
 *
 * TYPED MAPS OF FULL LITERAL KEYS, so every key is one `check-key-usage.mjs`
 * can see, and `dynamic-key-coverage.spec.ts` asserts each map covers exactly
 * its union and that every key exists in en.json (the other locales follow by
 * the `i18n:check` parity gate). Which item matters for what is the server's
 * answer (`strength`); these only name it.
 */
import type { IdentityDocumentFailureKind } from '@tonnie/heroes';
import type {
  IdentityDocumentApplyField,
  ProfileStrengthItemKey,
  ProfileStrengthLevel,
  ProfileStrengthSectionId,
  ProfileStrengthTag,
} from '@tonnie/types';

export const PROFILE_STRENGTH_ITEM_KEY = {
  name: 'profile.strength.item.name',
  date_of_birth: 'profile.strength.item.date_of_birth',
  phone: 'profile.strength.item.phone',
  residence: 'profile.strength.item.residence',
  nationality: 'profile.strength.item.nationality',
  passport: 'profile.strength.item.passport',
  work_model: 'profile.strength.item.work_model',
  current_role: 'profile.strength.item.current_role',
  years_experience: 'profile.strength.item.years_experience',
  skills: 'profile.strength.item.skills',
  languages: 'profile.strength.item.languages',
  education: 'profile.strength.item.education',
  experience: 'profile.strength.item.experience',
} as const satisfies Record<ProfileStrengthItemKey, string>;

/** Why an item matters — the pillar it feeds, or the gate it opens. */
export const PROFILE_STRENGTH_WHY_KEY = {
  name: 'profile.strength.why.start',
  date_of_birth: 'profile.strength.why.start',
  phone: 'profile.strength.why.start',
  residence: 'profile.strength.why.persona',
  nationality: 'profile.strength.why.persona',
  passport: 'profile.strength.why.passport',
  work_model: 'profile.strength.why.workModel',
  current_role: 'profile.strength.why.skills',
  years_experience: 'profile.strength.why.years',
  skills: 'profile.strength.why.skills',
  languages: 'profile.strength.why.languages',
  education: 'profile.strength.why.education',
  experience: 'profile.strength.why.experience',
} as const satisfies Record<ProfileStrengthItemKey, string>;

export const PROFILE_STRENGTH_TAG_KEY = {
  start: 'profile.strength.tag.start',
  score: 'profile.strength.tag.score',
  journey: 'profile.strength.tag.journey',
} as const satisfies Record<ProfileStrengthTag, string>;

export const PROFILE_STRENGTH_LEVEL_KEY = {
  getting_started: 'profile.strength.level.getting_started',
  good: 'profile.strength.level.good',
  strong: 'profile.strength.level.strong',
  complete: 'profile.strength.level.complete',
} as const satisfies Record<ProfileStrengthLevel, string>;

export const PROFILE_SECTION_TITLE_KEY = {
  identity: 'profile.sections.identity.title',
  mobility: 'profile.sections.mobility.title',
  skills: 'profile.sections.skills.title',
  education: 'profile.sections.education.title',
  experience: 'profile.sections.experience.title',
} as const satisfies Record<ProfileStrengthSectionId, string>;

/**
 * The lines of the passport / ID review, and of its "added to your profile"
 * list. `name` is the LEGAL name (as printed); `account_name` offers it for the
 * account — so they must read differently from the profile-strength "Name".
 */
export const IDENTITY_DOCUMENT_LINE_KEY = {
  name: 'profile.identityDocument.line.name',
  account_name: 'profile.identityDocument.line.account_name',
  date_of_birth: 'profile.identityDocument.line.date_of_birth',
  nationality: 'profile.identityDocument.line.nationality',
  passport: 'profile.identityDocument.line.passport',
} as const satisfies Record<IdentityDocumentApplyField, string>;

/**
 * What went wrong in the passport / ID flow: a refused read's kind, or the
 * upload or the save failing. `tooLarge` interpolates `{{size}}` (MB).
 */
export const IDENTITY_DOCUMENT_ERROR_KEY = {
  notIdentity: 'profile.identityDocument.error.notIdentity',
  unreadable: 'profile.identityDocument.error.unreadable',
  unsupported: 'profile.identityDocument.error.unsupported',
  tooLarge: 'profile.identityDocument.error.tooLarge',
  unavailable: 'profile.identityDocument.error.unavailable',
  generic: 'profile.identityDocument.error.generic',
  upload: 'profile.identityDocument.error.upload',
  save: 'profile.identityDocument.error.save',
} as const satisfies Record<IdentityDocumentFailureKind | 'upload' | 'save', string>;
